import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectDevices } from '../../../../../stores/reducers/devicesData/selector';
import { iFullStoreState } from '../../../../../shared/interfaces';
import { calculateServiceDueDate, groupServiceNeeded, IServiceItem, IServiceGroups } from '../utils';

const TARGET_FIELDS = ['service', 'oil chg/svc'];

export const useServiceNeededReport = (deviceIdsFilter: string[] = []) => {
    const [loading, setLoading] = useState(true);
    const devicesDetails = useSelector(selectDevices);
    const allExtraInfo = useSelector((state: iFullStoreState) => state.general.extraInfo);

    const reportData = useMemo<IServiceGroups | null>(() => {
        if (!devicesDetails || !allExtraInfo) return null;

        const serviceFieldKeys = Object.entries(allExtraInfo)
            .filter(([_, info]) => {
                const name = info?.details?.name?.toLowerCase() || '';
                return TARGET_FIELDS.some(target => name.includes(target));
            })
            .map(([key, info]) => ({ key, name: info.details.name }));

        if (serviceFieldKeys.length === 0) {
            return groupServiceNeeded([]);
        }

        const items: IServiceItem[] = [];
        const devicesArray = devicesDetails.valueSeq().toArray();

        const targetDevices = deviceIdsFilter.length > 0
            ? devicesArray.filter(d => deviceIdsFilter.includes(d.id))
            : devicesArray;

        targetDevices.forEach(device => {
            const extraInfo = device['extra-info'];
            if (!extraInfo) return;

            serviceFieldKeys.forEach(field => {
                const fieldData = extraInfo[field.key];
                const dateStr = typeof fieldData === 'object' && fieldData !== null ? fieldData.date : null;

                if (dateStr) {
                    const calcResult = calculateServiceDueDate(dateStr, 3);

                    if (calcResult && calcResult.daysLeft <= 30) {
                        items.push({
                            deviceId: device.id,
                            vehicleName: device.name,
                            itemName: field.name,
                            lastServiceDate: dateStr,
                            dueDate: calcResult.dueDateStr,
                            daysLeft: calcResult.daysLeft
                        });
                    }
                }
            });
        });

        return groupServiceNeeded(items);
    }, [devicesDetails, allExtraInfo, deviceIdsFilter]);

    useEffect(() => {
        if (devicesDetails && allExtraInfo) {
            setLoading(false);
        }
    }, [devicesDetails, allExtraInfo]);

    return { reportData, loading };
};